import Separator from "@components/CustomTypography/Separator/Separator"
import ButtonComponent from "@components/Theme/Button/Button"
import {AnimatePresence, LayoutGroup, motion} from "framer-motion"
import React, {ReactNode, useEffect, useState} from "react"

import styles from "../FeaturesGrid.module.scss"

import DefaultNavigationArrows from "./DefaultNavigationArrows"
import ZoomedCard from "./ZoomedCard"

type Category<T> = {
	name: string
	content: T[]
}

type ReusableFeaturesGridProps<T> = {
	categories: Category<T>[]
	renderItem: (
		item: T,
		index: number,
		hoveredIndex: number | null,
	) => ReactNode
	renderShadowItem?: (item: T, index: number) => ReactNode
	renderPopup?: (item: T, index: number, onClose: () => void) => ReactNode
	renderZoomedContent?: (item: T) => ReactNode
	renderArrows?: (
		activeIndex: number | null,
		setActiveIndex: (i: number | null) => void,
		items: T[],
	) => ReactNode
}

export function ReusableFeaturesGrid<T>({
	categories,
	renderItem,
	renderShadowItem,
	renderPopup,
	renderZoomedContent,
	renderArrows,
}: ReusableFeaturesGridProps<T>) {
	const [activeCategory, setActiveCategory] = useState(0)
	const [activeIndex, setActiveIndex] = useState<number | null>(null)
	const [hoveredIndex] = useState<number | null>(null)
	const [closingIndex, setClosingIndex] = useState<number | null>(null)

	const currentItems = categories[activeCategory]?.content ?? []

	useEffect(() => {
		if (activeIndex === null) return

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "ArrowRight") {
				setActiveIndex((prev) =>
					prev !== null ? (prev + 1) % currentItems.length : 0,
				)
			} else if (e.key === "ArrowLeft") {
				setActiveIndex((prev) =>
					prev !== null
						? (prev - 1 + currentItems.length) % currentItems.length
						: currentItems.length - 1,
				)
			} else if (e.key === "Escape") {
				setClosingIndex(activeIndex)
				setActiveIndex(null)
			}
		}

		window.addEventListener("keydown", handleKeyDown)
		return () => window.removeEventListener("keydown", handleKeyDown)
	}, [activeIndex, currentItems.length])

	return (
		<LayoutGroup>
			<div className={styles.wrapper}>
				{/* Tabs */}
				<div className={styles.tabs}>
					{categories.map((cat, i) => (
						<ButtonComponent
							key={i}
							buttonType={"primary"}
							contrast={i !== activeCategory}
							className={styles.tab}
							onClick={() => setActiveCategory(i)}
						>
							{cat.name}
						</ButtonComponent>
					))}
				</div>

				<Separator size={32} />
				<Separator size={16} />

				{/* Grid */}
				<div className={styles.grid}>
					<AnimatePresence
						mode={"wait"}
						onExitComplete={() => setClosingIndex(null)}
					>
						{currentItems.map((item, index) => {
							const isActive = activeIndex === index

							return (
								<motion.div
									key={`item-${activeCategory}-${index}`}
									layout
									layoutId={`feature-popup-${activeCategory}-${index}`}
									className={`${styles.card} ${
										isActive ? styles.transparentCard : ""
									}`}
									style={{
										zIndex: closingIndex === index ? 10 : 0,
									}}
									variants={{
										hidden: {opacity: 0, scale: 0.9},
										visible: (i: number) => ({
											opacity: 1,
											scale: 1,
											transition: {delay: i * 0.05},
										}),
										exit: {opacity: 0, scale: 0.9},
									}}
									custom={index}
									initial={"hidden"}
									animate={"visible"}
									exit={"exit"}
									onClick={() => setActiveIndex(index)}
								>
									{renderItem(item, index, hoveredIndex)}
								</motion.div>
							)
						})}
					</AnimatePresence>
				</div>

				{/* Shadow DOM for SEO */}
				<div style={{display: "none"}} aria-hidden={"true"}>
					{categories.map((category, catIndex) =>
						catIndex !== activeCategory
							? category.content.map((item, index) => (
									<div
										key={`shadow-${catIndex}-${index}`}
										className={styles.card}
									>
										{renderShadowItem
											? renderShadowItem(item, index)
											: renderItem(item, index, null)}
									</div>
							  ))
							: null,
					)}
				</div>

				{/* Zoomed Card or Popup */}
				{activeIndex !== null &&
					currentItems[activeIndex] &&
					(renderPopup ? (
						renderPopup(
							currentItems[activeIndex],
							activeIndex,
							() => {
								setClosingIndex(activeIndex)

								setActiveIndex(null)
							},
						)
					) : (
						<ZoomedCard
							activeIndex={activeIndex}
							layoutId={`feature-popup-${activeCategory}-${activeIndex}`}
							onClose={() => {
								setClosingIndex(activeIndex)

								setActiveIndex(null)
							}}
						>
							{renderZoomedContent?.(currentItems[activeIndex])}
						</ZoomedCard>
					))}

				{/* Arrows */}
				{renderArrows ? (
					renderArrows(activeIndex, setActiveIndex, currentItems)
				) : (
					<DefaultNavigationArrows
						activeIndex={activeIndex}
						data={currentItems}
						setActiveIndex={setActiveIndex}
					/>
				)}
			</div>
		</LayoutGroup>
	)
}
